import type { StalledAdopter } from './types';

export const STALLED_REMINDER_EMAIL_SUBJECT = 'Set up your Fi Mini GPS Tracker';

/**
 * The email the rescue sends from its own inbox. Plain text, so it pastes cleanly into any mail client,
 * and signed by the rescue, since replies go to the rescue rather than to Fi.
 */
export function stalledReminderEmailBody(orgName: string): string {
  return `Hello from ${orgName},

Congratulations again on your adoption! Your new companion came home with a Fi Mini GPS Tracker, and it isn't activated yet.

To activate the tracker and start tracking, download the Fi app and follow the setup instructions in your Fi email:
https://fitracking.com/app

If you've already set it up, just reply and let us know.

Best,
${orgName}`;
}

/** Has a number to text and hasn't replied STOP. */
export function canText(adopter: StalledAdopter): boolean {
  return !!adopter.phoneNumber && !adopter.smsOptedOut;
}

/**
 * Text all's list order: whoever was texted most recently first, so the people a rescue might want to
 * leave out are at the top, and never-texted adopters after them. Then the adopters who can't be texted:
 * those who opted out, then those with no number. Ties keep the server's order, freshest stall first.
 */
export function orderForTexting(adopters: StalledAdopter[]): StalledAdopter[] {
  const lastTexted = (adopter: StalledAdopter) =>
    adopter.lastTextReminderAt ? new Date(adopter.lastTextReminderAt).getTime() : -Infinity;
  return [
    ...adopters.filter(canText).sort((a, b) => lastTexted(b) - lastTexted(a)),
    ...adopters.filter((adopter) => adopter.phoneNumber && adopter.smsOptedOut),
    ...adopters.filter((adopter) => !adopter.phoneNumber),
  ];
}

/** Every address once, compared case-insensitively, in list order. */
export function distinctEmails(adopters: StalledAdopter[]): string[] {
  const seen = new Set<string>();
  return adopters
    .flatMap((adopter) => adopter.emails)
    .filter((email) => {
      const key = email.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
}

export type SmsTemplatePart = { text: string } | { token: 'adopterFirstName' | 'petName' };

/** Splits the server's template around its placeholders, so they can render as locked tokens. */
export function splitSmsTemplate(template: string): SmsTemplatePart[] {
  return template
    .split(/(\{adopterFirstName\}|\{petName\})/)
    .filter((piece) => piece !== '')
    .map((piece) => {
      if (piece === '{adopterFirstName}') {
        return { token: 'adopterFirstName' as const };
      }
      if (piece === '{petName}') {
        return { token: 'petName' as const };
      }
      return { text: piece };
    });
}

/** Whole days since `dateStr`, for "stalled 9 days". */
export function daysSince(dateStr: string, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(dateStr).getTime()) / (24 * 60 * 60 * 1000)));
}

/** The request id a send is recorded under. One per send, so a double submit can't text anyone twice. */
export function newReminderRequestId(): string {
  return crypto.randomUUID();
}
